// src/components/CookieConsent.tsx
import { useEffect, useState } from "react";
import { Button } from "@appcaire/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@appcaire/ui";
import { getCookie, setCookie } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { updateConsent } from "@/lib/analytics";
import { CONSENT_COOKIE, LEGACY_CONSENT_COOKIE } from "@/lib/cookie-utils";
import { Cookie, Shield, BarChart3 } from "lucide-react";

const COOKIE_CONSENT_KEY = CONSENT_COOKIE;
const COOKIE_EXPIRY_DAYS = 365;

export function CookieConsent() {
  const [mounted, setMounted] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { t } = useTranslation(["common", "analytics"]);

  useEffect(() => {
    setMounted(true);

    // Small delay to ensure analytics are fully initialized
    const initializeConsent = () => {
      const consent = getCookie(COOKIE_CONSENT_KEY);
      if (getCookie(LEGACY_CONSENT_COOKIE) !== null)
        setCookie(LEGACY_CONSENT_COOKIE, "", -1);

      if (!consent) {
        // No decision yet: Google Analytics, Google Ads and the ChatGPT Ads
        // pixel all stay off until the visitor accepts all in the banner.
        setShowConsent(true);
      } else {
        // User has already made a decision, update consent accordingly
        const shouldGrantConsent = consent === "all";
        console.log(
          "Existing consent found:",
          consent,
          "granting analytics:",
          shouldGrantConsent,
        );
        updateConsent(shouldGrantConsent);
      }
    };

    // Initialize consent after a small delay to ensure analytics are ready
    const timeoutId = setTimeout(initializeConsent, 100);

    // Add global event listener for reopening the dialog
    const handleReopenDialog = () => {
      setShowConsent(true);
      setShowDetails(false);
    };

    // Listen for custom event to reopen dialog
    window.addEventListener("openCookieConsent", handleReopenDialog);

    // Listen for clicks on cookie button
    const cookieButton = document.getElementById("cookie-btn");
    if (cookieButton) {
      cookieButton.addEventListener("click", handleReopenDialog);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("openCookieConsent", handleReopenDialog);
      if (cookieButton) {
        cookieButton.removeEventListener("click", handleReopenDialog);
      }
    };
  }, []);

  const handleAcceptAll = () => {
    console.log("User accepted all cookies");
    setCookie(COOKIE_CONSENT_KEY, "all", COOKIE_EXPIRY_DAYS);
    setShowConsent(false);
    // Grant analytics consent
    updateConsent(true);
  };

  const handleRejectAll = () => {
    console.log("User rejected analytics cookies");
    setCookie(COOKIE_CONSENT_KEY, "necessary", COOKIE_EXPIRY_DAYS);
    setShowConsent(false);
    // Deny analytics consent
    updateConsent(false);
  };

  const handleAcceptNecessary = () => {
    console.log("User accepted only necessary cookies");
    setCookie(COOKIE_CONSENT_KEY, "necessary", COOKIE_EXPIRY_DAYS);
    setShowConsent(false);
    // Deny analytics consent
    updateConsent(false);
  };

  // Don't render anything until mounted to prevent hydration issues
  if (!mounted) return null;

  return (
    <Dialog
      open={showConsent}
      onOpenChange={() => {
        /* Prevent closing without choice */
      }}
    >
      <DialogContent className="rounded-[20px] border border-[var(--cb-border-soft)] bg-caire-page text-caire-ink shadow-[0_12px_40px_-16px_hsl(var(--cb-ink-hsl)/0.25)] sm:max-w-[500px]">
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-2">
            <Cookie className="h-5 w-5 text-coral-text" />
            <DialogTitle className="text-lg font-semibold text-caire-ink">
              {t("cookies.title")}
            </DialogTitle>
          </div>
          <DialogDescription className="text-base leading-relaxed text-slate">
            {t("cookies.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Necessary Cookies - Always Required */}
          <div className="flex items-start gap-3 rounded-xl border border-[var(--cb-border-soft)] bg-white p-4">
            <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-coral-text" />
            <div className="space-y-2 flex-1">
              <div className="flex items-center justify-between">
                <div
                  className="text-sm font-semibold text-caire-ink"
                  role="heading"
                  aria-level={3}
                >
                  {t("cookies.necessary.title")}
                </div>
                <span className="rounded-full border border-caire-hairline bg-caire-sand px-2 py-1 text-xs font-medium text-caire-ink">
                  {t("cookies.necessary.required")}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-slate">
                {t("cookies.necessary.description")}
              </p>
            </div>
          </div>

          {/* Analytics Cookies - Optional */}
          <div className="flex items-start gap-3 rounded-xl border border-[var(--cb-border-soft)] bg-white p-4">
            <BarChart3 className="mt-0.5 h-5 w-5 flex-shrink-0 text-coral-text" />
            <div className="space-y-2 flex-1">
              <div className="flex items-center justify-between">
                <div
                  className="text-sm font-semibold text-caire-ink"
                  role="heading"
                  aria-level={3}
                >
                  {t("cookies.analytics.title")}
                </div>
                <span className="rounded-full border border-[var(--cb-border-soft)] bg-[var(--cb-accent-pale)] px-2 py-1 text-xs font-medium text-[var(--cb-action)]">
                  {t("cookies.analytics.optional")}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-slate">
                {t("cookies.analytics.description")}
              </p>
            </div>
          </div>

          {/* Privacy Policy Link */}
          <div className="text-center">
            <button
              type="button"
              aria-expanded={showDetails}
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm font-medium text-[var(--cb-action)] underline-offset-4 transition-colors hover:underline"
            >
              {t("cookies.details.toggle")}
            </button>
          </div>

          {showDetails && (
            <div className="space-y-2 rounded-xl border border-[var(--cb-border-soft)] bg-white p-4 text-xs text-slate">
              <p className="font-medium text-caire-ink">
                {t("cookies.details.recipients.title")}
              </p>
              <ul className="list-disc space-y-1 pl-4">
                <li>{t("cookies.details.recipients.google")}</li>
                <li>{t("cookies.details.recipients.openAi")}</li>
              </ul>
              <p>{t("cookies.details.info")}</p>
              <p>{t("cookies.details.control")}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          {/* Equal prominence buttons as per GDPR */}
          <Button
            variant="outline"
            onClick={handleRejectAll}
            className="flex-1 border border-caire-ink bg-white font-semibold text-caire-ink hover:bg-caire-sand"
          >
            {t("cookies.reject.button")}
          </Button>
          <Button
            onClick={handleAcceptAll}
            className="flex-1 bg-[var(--cb-action)] font-semibold text-white hover:bg-caire-action-hover"
          >
            {t("cookies.accept.button")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
